/**
 * Approvals inbox (/audit/advanced/actions): every pending remediation proposal across findings, with expiry
 * countdown, the exact change per item, bulk approve of LOW-risk items behind one MFA step-up, and history.
 * Medium/high-risk items are approved one at a time after reading the diff (approve-first, spec §1).
 */
import { useEffect, useId, useMemo, useState, useSyncExternalStore } from 'react';
import type { RemediationProposal } from '@qm/contracts/audit-remediation';
import { Badge, Button, DataTable, EmptyState, ErrorState, Heading, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import { failureOf } from './client';
import { AdvancedPage, AUDIT_FLAGS, FailureState, useRemediationClient, useRoles } from './context';
import { DiffViewer } from './DiffViewer';
import { riskTone } from './ResolvePanel';
import { bulkEligible, createInboxFlow, isPending, liveStatus, timeLeft, type InboxFlow, type InboxState } from './resolve-flow';
import { StepUpPrompt } from './StepUpPrompt';
import './actions.css';

export function ApprovalsInboxPage() {
  return <AdvancedPage area="actions" flag={AUDIT_FLAGS.remediation}><ApprovalsInbox /></AdvancedPage>;
}

export function ApprovalsInbox({ now: fixedNow }: { now?: number }) {
  const client = useRemediationClient();
  const [now, setNow] = useState(() => fixedNow ?? Date.now());
  const flow = useMemo(() => createInboxFlow({ client, now: () => fixedNow ?? Date.now() }), [client, fixedNow]);
  const state = useSyncExternalStore(flow.subscribe, flow.getState, flow.getState);
  useEffect(() => { void flow.load(); }, [flow]);
  useEffect(() => {
    if (fixedNow !== undefined) return;
    const h = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(h);
  }, [fixedNow]);
  return <InboxView state={state} flow={flow} now={now} />;
}

const countdown = (t: ReturnType<typeof useApp>['t'], expiresAt: string, now: number) => {
  const l = timeLeft(expiresAt, now);
  if (l.expired) return t('audit-remediation.actions.inbox.expired');
  return l.days > 0 ? t('audit-remediation.actions.inbox.leftDays', { d: l.days, h: l.hours }) : t('audit-remediation.actions.inbox.leftHours', { h: l.hours, m: l.minutes });
};

/** Pure view (tests render it for every state). */
export function InboxView({ state, flow, now }: { state: InboxState; flow: Pick<InboxFlow, 'toggle' | 'selectAllLowRisk' | 'clearSelection' | 'bulkApprove' | 'approve' | 'reject' | 'submitStepUp' | 'cancelStepUp'>; now: number }) {
  const { t } = useApp();
  const f = useFormat();
  const roles = useRoles();
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const tabsId = useId();
  if (state.phase === 'loading') return <Loading label={t('common.loading')} />;
  if (state.phase === 'error') return <FailureState failure={failureOf(state.failure)} />;
  const pending = state.items.filter((q) => isPending(q, now));
  const history = state.items.filter((q) => !isPending(q, now));
  const eligible = pending.filter((q) => bulkEligible(q, now));
  const provider = (q: RemediationProposal) => t(`audit-remediation.provider.${q.provider}`);
  return (
    <div className="ar-inbox">
      <div className="ar-segment" role="tablist" aria-label={t('audit-remediation.actions.inbox.tabs')}>
        {(['pending', 'history'] as const).map((k) => (
          <button key={k} type="button" role="tab" id={`${tabsId}-${k}`} aria-controls={`${tabsId}-${k}-panel`} aria-selected={tab === k} className="ar-segment__tab" onClick={() => setTab(k)}>
            {t(`audit-remediation.actions.inbox.${k}`, { n: k === 'pending' ? pending.length : history.length })}
          </button>
        ))}
      </div>
      <p role="status" aria-live="polite" className="ar-muted">{state.approved ? t('audit-remediation.actions.inbox.approvedN', { n: state.approved }) : ''}</p>
      {state.error ? <ErrorState message={t(state.error)} /> : null}
      {state.pending ? (
        <StepUpPrompt action={state.pending.kind === 'bulk' ? 'bulk' : 'approve'} busy={state.busy} error={state.stepUpError} onSubmit={(c) => void flow.submitStepUp(c)} onCancel={flow.cancelStepUp} />
      ) : null}
      {tab === 'pending' ? (
        <div role="tabpanel" id={`${tabsId}-pending-panel`} aria-labelledby={`${tabsId}-pending`}>
          {!pending.length ? <EmptyState title={t('audit-remediation.actions.inbox.empty')}><p>{t('audit-remediation.actions.inbox.emptyBody')}</p></EmptyState> : (
            <>
              {roles.manage && eligible.length ? (
                <div className="ar-bulk" data-testid="bulk-bar">
                  <p>{t('audit-remediation.actions.inbox.bulkHint', { n: eligible.length })}</p>
                  <div className="cu-btnrow">
                    <Button onClick={state.selected.length ? flow.clearSelection : flow.selectAllLowRisk}>
                      {t(state.selected.length ? 'audit-remediation.actions.inbox.clear' : 'audit-remediation.actions.inbox.selectLow')}
                    </Button>
                    <Button variant="accent" disabled={!state.selected.length || state.busy} onClick={() => void flow.bulkApprove()} data-testid="bulk-approve">
                      {t('audit-remediation.actions.inbox.bulkApprove', { n: state.selected.length })}
                    </Button>
                  </div>
                </div>
              ) : null}
              <ul className="ar-cards">
                {pending.map((q) => {
                  const lowRisk = bulkEligible(q, now);
                  const left = timeLeft(q.expiresAt, now);
                  return (
                    <li key={q.id} className="ar-card" data-testid={`proposal-${q.id}`}>
                      <div className="ar-card__head">
                        {roles.manage ? (
                          <label className="ar-check">
                            <input type="checkbox" disabled={!lowRisk} checked={state.selected.includes(q.id)} onChange={() => flow.toggle(q.id)} />
                            <span className="qm-sr-only">{t('audit-remediation.actions.inbox.select', { target: f.text(q.target.label ?? q.target.ref) })}</span>
                          </label>
                        ) : null}
                        <Heading level={2} className="ar-card__title"><bdi>{f.text(q.target.label ?? q.target.ref)}</bdi></Heading>
                        <Badge tone={riskTone(q.risk)}>{t(`audit-remediation.risk.${q.risk}`)}</Badge>
                        <span className="ar-countdown" data-urgent={left.urgent ? 'true' : undefined}>{countdown(t, q.expiresAt, now)}</span>
                      </div>
                      <p className="ar-muted">{provider(q)} · <bdi>{f.text(q.actionKind)}</bdi> · {t('audit-remediation.actions.inbox.finding', { id: q.findingId })}</p>
                      {!lowRisk ? <p className="ar-muted">{t('audit-remediation.actions.inbox.oneByOne')}</p> : null}
                      <details className="ar-card__diff">
                        <summary>{t('audit-remediation.actions.inbox.viewChange')}</summary>
                        <DiffViewer before={q.before} after={q.after} diff={q.diff} />
                      </details>
                      {roles.manage ? (
                        <div className="cu-btnrow">
                          <Button variant="accent" disabled={state.busy} onClick={() => void flow.approve(q.id)} data-testid={`approve-${q.id}`}>{t('audit-remediation.actions.resolve.approve')}</Button>
                          <Button disabled={state.busy} onClick={() => void flow.reject(q.id)} data-testid={`reject-${q.id}`}>{t('audit-remediation.actions.resolve.reject')}</Button>
                        </div>
                      ) : <p className="ar-muted">{t('audit-remediation.errors.notApprover')}</p>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      ) : (
        <div role="tabpanel" id={`${tabsId}-history-panel`} aria-labelledby={`${tabsId}-history`}>
          {!history.length ? <EmptyState title={t('audit-remediation.actions.inbox.noHistory')} /> : (
            <DataTable caption={t('audit-remediation.actions.inbox.historyCaption')} rows={history} columns={[
              { key: 'target', header: t('audit-remediation.actions.preview.target'), render: (q) => <bdi>{f.text(q.target.label ?? q.target.ref)}</bdi> },
              { key: 'provider', header: t('audit-remediation.actions.preview.provider'), render: provider },
              { key: 'status', header: t('audit-remediation.actions.inbox.status'), render: (q) => <Badge tone={q.status === 'verified' ? 'success' : q.status === 'failed' ? 'error' : 'neutral'}>{t(`audit-remediation.actions.live.${q.status === 'proposed' ? 'expired' : liveStatus(q)}`)}</Badge> },
              { key: 'decidedAt', header: t('audit-remediation.actions.inbox.decided'), render: (q) => f.dateTime(q.decidedAt ?? q.updatedAt) },
            ]} />
          )}
        </div>
      )}
    </div>
  );
}
